import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import ActionToast from "../../../../shared/components/ActionToast";
import Loader from "../../../../shared/components/Loader";
import TableEmpty from "../../../../shared/components/tables/TableEmpty";
import BoxSearchIcon from "../../../../shared/assets/icons/BoxSearchIcon";
import Table from "../../../../shared/components/tables/Table";
import SortTableHead, { TableColumnDefinition } from "../../../../shared/components/SortTableHead";
import {
  StatusTypeCode,
  EMPLOYEE_SORT_PARAMETERS,
  createVariantMap,
  createLabelMap,
  ReinviteUser,
  PinRegenerate,
  EnableUser,
  DisableUser,
} from "../../models/userModels";
import {
  clearError,
  clearSuccess,
  deleteUser,
  getUsers,
  setSort,
  enableUser,
  disableUser,
  reinviteUser,
  pinRegenerate,
} from "../../customerUsersSlice";
import useTranslation from "../../../../shared/hooks/useTranslation";
import PermanentEmployeeInviteDialog from "../invitations/PermanentEmployeeInviteDialog";
import TemporaryUserInviteDialogue from "../invitations/TemporaryUserInviteDialogue";
import { EmployeeTableBody } from "../employees/EmployeeTableBody";
import { useEmployeeTime } from "../../hooks/useEmployeeTime";
import { getStatusLabel, sortUsers } from "../../utils/employeeUtils";
import MenuButton from "../../../../shared/components/MenuButton";
import { getNotificationMessage } from "../../../../shared/utils/notificationUtils";
import accountContext from "../../../../account/accountContext";
import { NotificationMessage } from "../../../../shared/models/models";
import SettingsCard from "../shared/SettingsCard";
import { dt } from "../../../../shared/themes/redesignTokens";

const SettingsTeam: React.FC = () => {
  const { users, loading, success, error, sortBy, sortDesc } = useAppSelector(
    (state) => state.customerUsers
  );
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [isPermanentDialogOpen, setIsPermanentDialogOpen] = useState(false);
  const [isTemporaryDialogOpen, setIsTemporaryDialogOpen] = useState(false);

  const { getTimeRemaining } = useEmployeeTime();

  const columns: TableColumnDefinition[] = useMemo(
    () => [
      { title: translate("employee"), parameter: EMPLOYEE_SORT_PARAMETERS.EMPLOYEE, columnAlign: "left" },
      { title: translate("department"), parameter: EMPLOYEE_SORT_PARAMETERS.DEPARTMENT, columnAlign: "left" },
      { title: translate("access"), parameter: EMPLOYEE_SORT_PARAMETERS.ACCESS, columnAlign: "left" },
      { title: translate("joined"), parameter: EMPLOYEE_SORT_PARAMETERS.JOINED, columnAlign: "left" },
      { title: translate("validUntil"), parameter: EMPLOYEE_SORT_PARAMETERS.VALID_UNTIL, columnAlign: "left" },
      { title: translate("status"), parameter: EMPLOYEE_SORT_PARAMETERS.STATUS, columnAlign: "left" },
      { title: translate("actions"), columnAlign: "right", twoLevelsColumWidth: "80px" },
    ],
    [translate]
  );

  const variantMap = useMemo(() => createVariantMap(), []);
  const labelMap = useMemo(() => createLabelMap(translate), [translate]);

  const sortedUsers = useMemo(
    () => sortUsers(users, sortBy || 0, sortDesc),
    [users, sortBy, sortDesc]
  );

  const getStatusLabelMemo = useCallback(
    (status: StatusTypeCode) => getStatusLabel(status, translate),
    [translate]
  );

  const handleSort = useCallback(
    (parameter: number) => {
      if (sortBy === parameter) {
        dispatch(setSort({ sortBy: parameter, sortDesc: !sortDesc }));
      } else {
        dispatch(setSort({ sortBy: parameter, sortDesc: false }));
      }
    },
    [sortBy, sortDesc, dispatch]
  );

  const handleReinviteUser = useCallback(
    async (data: ReinviteUser) => {
      await dispatch(reinviteUser(data));
    },
    [dispatch]
  );

  const handleDeleteUser = useCallback(
    async (id: string) => {
      if (!id) return;
      const deleteResult = await dispatch(deleteUser({ id }));
      if (deleteUser.fulfilled.match(deleteResult)) {
        if (id === accountContext.user?.id) {
          accountContext.logout();
        }
        await dispatch(getUsers());
      }
    },
    [dispatch]
  );

  const handlePinRegenerate = useCallback(
    async (data: PinRegenerate) => {
      await dispatch(pinRegenerate(data));
      await dispatch(getUsers());
    },
    [dispatch]
  );

  const handleEnableTemporaryAccess = useCallback(
    async (data: EnableUser) => {
      await dispatch(enableUser(data));
      await dispatch(getUsers());
    },
    [dispatch]
  );

  const handleDisableTemporaryAccess = useCallback(
    async (data: DisableUser) => {
      await dispatch(disableUser(data));
      if (data.userId === accountContext.user?.id) {
        accountContext.logout();
      }
      await dispatch(getUsers());
    },
    [dispatch]
  );

  const handleOpenPermanentDialog = useCallback(() => setIsPermanentDialogOpen(true), []);
  const handleClosePermanentDialog = useCallback(
    (shouldRefresh?: boolean) => {
      setIsPermanentDialogOpen(false);
      if (shouldRefresh) dispatch(getUsers());
    },
    [dispatch]
  );

  const handleOpenTemporaryDialog = useCallback(() => setIsTemporaryDialogOpen(true), []);
  const handleCloseTemporaryDialog = useCallback(
    (shouldRefresh?: boolean) => {
      setIsTemporaryDialogOpen(false);
      if (shouldRefresh) dispatch(getUsers());
    },
    [dispatch]
  );

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const inviteButton = (
    <MenuButton title={translate("invite")} width="200px">
      <MenuItem onClick={handleOpenPermanentDialog}>{translate("permanentEmployee")}</MenuItem>
      <MenuItem onClick={handleOpenTemporaryDialog}>{translate("temporaryAccess")}</MenuItem>
    </MenuButton>
  );

  // Table styling
  const tableSx = {
    "& thead th": {
      backgroundColor: dt.headerBg,
      borderBottom: `1px solid ${dt.cardBorder}`,
      fontSize: "11px !important",
      fontWeight: "700 !important",
      textTransform: "uppercase",
      letterSpacing: "1.2px",
      color: "#64748B !important",
      fontFamily: `${dt.font} !important`,
      padding: "10px 12px",
      whiteSpace: "nowrap",
    },
    "& tbody tr": {
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        backgroundColor: dt.rowHover,
        boxShadow: `inset 3px 0 0 0 ${dt.blue}`,
      },
    },
    "& tbody td": {
      fontFamily: `${dt.font} !important`,
      fontSize: "13px",
      padding: "10px 12px",
      borderBottom: `1px solid ${dt.rowBorder}`,
    },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={getNotificationMessage(success, translate as (key: string, params?: any) => string)}
        onClose={() => dispatch(clearSuccess(undefined))}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={translate(NotificationMessage.error(error).message)}
        onClose={() => dispatch(clearError(undefined))}
        duration={6000}
      />

      <SettingsCard
        title={translate("settings:team.title")}
        subtitle={translate("settings:team.description")}
        action={inviteButton}
        animationDelay="0.15s"
      >
        {loading ? (
          <Loader />
        ) : !users?.length ? (
          <TableEmpty iconComponent={BoxSearchIcon} />
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto", ...tableSx }}>
            <Table>
              <SortTableHead
                columns={columns}
                sortBy={sortBy}
                sortDesc={sortDesc}
                onSort={handleSort}
              />
              <EmployeeTableBody
                users={sortedUsers}
                variantMap={variantMap}
                labelMap={labelMap}
                onReinviteUser={handleReinviteUser}
                onDeleteUser={handleDeleteUser}
                getTimeRemaining={getTimeRemaining}
                onPinRegenerate={handlePinRegenerate}
                onEnableTemporaryAccess={handleEnableTemporaryAccess}
                onDisableTemporaryAccess={handleDisableTemporaryAccess}
                getStatusLabel={getStatusLabelMemo}
                translate={translate as (key: string, params?: any) => string}
                translateCommon={tCommon as (key: string, params?: any) => string}
              />
            </Table>
          </Box>
        )}
      </SettingsCard>

      <PermanentEmployeeInviteDialog
        open={isPermanentDialogOpen}
        onClose={handleClosePermanentDialog}
      />
      <TemporaryUserInviteDialogue
        open={isTemporaryDialogOpen}
        onClose={handleCloseTemporaryDialog}
      />
    </Box>
  );
};

export default SettingsTeam;
